export type ToastVariant = 'success' | 'error' | 'info' | 'loading';

export type ToastAction = {
  label: string;
  onclick: () => void;
};

export type ToastItem = {
  id: string;
  variant: ToastVariant;
  message: string;
  action?: ToastAction;
  dismissible?: boolean;
  duration: number;
  isPaused?: boolean;
};

const MAX_TOASTS = 3;

class ToastStore {
  items = $state<ToastItem[]>([]);
  private timers = new Map<string, number>();

  private nextId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  private push(
    variant: ToastVariant,
    message: string,
    opts: { action?: ToastAction; duration?: number; dismissible?: boolean } = {}
  ): string {
    const id = this.nextId();
    const duration =
      opts.duration ??
      (variant === 'error' ? 6000 : variant === 'loading' ? 0 : 4000);
    const item: ToastItem = {
      id,
      variant,
      message,
      action: opts.action,
      dismissible: opts.dismissible ?? true,
      duration
    };

    // Si ya hay MAX_TOASTS, quitar el más antiguo
    if (this.items.length >= MAX_TOASTS) {
      const oldest = this.items[0];
      if (oldest) this.dismiss(oldest.id);
    }

    this.items = [...this.items, item];

    // Auto-dismiss (excepto loading o duración 0). Permitimos que toasts con
    // acción (Undo) también desaparezcan para no bloquear la UI.
    if (duration > 0 && variant !== 'loading') {
      const t = window.setTimeout(() => this.dismiss(id), duration);
      this.timers.set(id, t);
    }

    return id;
  }

  dismiss(id: string) {
    const t = this.timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(id);
    }
    this.items = this.items.filter((it) => it.id !== id);
  }

  clear() {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    this.items = [];
  }

  pause(id: string) {
    const t = this.timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(id);
    }
    this.items = this.items.map(it => it.id === id ? { ...it, isPaused: true } : it);
  }

  resume(id: string) {
    const item = this.items.find((it) => it.id === id);
    if (!item || item.duration <= 0 || item.action) return;
    const t = window.setTimeout(() => this.dismiss(id), item.duration);
    this.timers.set(id, t);
    this.items = this.items.map(it => it.id === id ? { ...it, isPaused: false } : it);
  }

  success(message: string, opts?: { action?: ToastAction; duration?: number }): string {
    return this.push('success', message, opts);
  }

  error(message: string, opts?: { action?: ToastAction; duration?: number }): string {
    return this.push('error', message, opts);
  }

  info(message: string, opts?: { action?: ToastAction; duration?: number }): string {
    return this.push('info', message, opts);
  }

  loading(message: string): string {
    return this.push('loading', message, { duration: 0, dismissible: false });
  }

  resolve(
    id: string,
    next: { variant: ToastVariant; message: string; duration?: number }
  ): void {
    const item = this.items.find((it) => it.id === id);
    if (!item) return;
    const t = this.timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(id);
    }
    const duration = next.duration ?? (next.variant === 'error' ? 6000 : 4000);
    this.items = this.items.map((it) =>
      it.id === id
        ? { ...it, variant: next.variant, message: next.message, duration }
        : it
    );
    if (duration > 0 && next.variant !== 'loading') {
      const newT = window.setTimeout(() => this.dismiss(id), duration);
      this.timers.set(id, newT);
    }
  }
}

export const toast = new ToastStore();